import { Card } from "@/components/ui/card";
import { CheckCircle, Circle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

interface JobProgressTrackerProps {
  hasPhotos: boolean;
  hasMeasurements: boolean;
  hasEstimate: boolean;
  hasProposal: boolean;
  isApproved: boolean;
}

export const JobProgressTracker = ({
  hasPhotos,
  hasMeasurements,
  hasEstimate,
  hasProposal,
  isApproved,
}: JobProgressTrackerProps) => {
  const steps = [
    { label: "Photos Captured", completed: hasPhotos, icon: "📸" },
    { label: "Measurements Done", completed: hasMeasurements, icon: "📐" },
    { label: "Estimate Created", completed: hasEstimate, icon: "💰" },
    { label: "Proposal Sent", completed: hasProposal, icon: "📄" },
    { label: "Customer Approved", completed: isApproved, icon: "✅" },
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const progressPercentage = (completedCount / steps.length) * 100;

  return (
    <Card className="p-6 border-secondary/20 bg-gradient-to-br from-secondary/5 via-card to-primary/5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-display font-bold">Job Progress</h3>
          <p className="text-xs text-muted-foreground">
            {completedCount} of {steps.length} steps completed
          </p>
        </div>
        <div className="text-3xl font-bold text-primary">
          {Math.round(progressPercentage)}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-2 bg-secondary/20 rounded-full overflow-hidden mb-6">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-secondary transition-all duration-500"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div
              className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all",
                step.completed
                  ? "bg-success/20 text-success"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {step.completed ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <Circle className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-lg">{step.icon}</span>
                <span
                  className={cn(
                    "text-sm font-medium",
                    step.completed ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
